// AdminTradingHallPage — Live multi-account view, kill all, pause all
// Bird's-eye view of all open positions across all traders

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface LiveAcct {
  id: string;
  trader: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  openPnl: number;
  positions: { symbol: string; side: 'long' | 'short'; pnl: number; size: number }[];
  lastUpdate: number;
}

const ACCOUNTS: LiveAcct[] = [
  { id: 'a1', trader: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, openPnl: 365,   lastUpdate: 2, positions: [{ symbol: 'ES', side: 'long', pnl: 365, size: 2 }] },
  { id: 'a2', trader: 'Sarah C.',  avatar: 2, account: 'FP-150K-12844', tier: 'Professional', accountSize: 150000, openPnl: 1240,  lastUpdate: 5, positions: [{ symbol: 'NQ', side: 'long', pnl: 580, size: 1 }, { symbol: 'GC', side: 'long', pnl: 660, size: 1 }] },
  { id: 'a3', trader: 'Riley T.',  avatar: 3, account: 'FP-50K-15201',  tier: 'Beginner',     accountSize: 50000,  openPnl: -240,  lastUpdate: 8, positions: [{ symbol: 'CL', side: 'short', pnl: -240, size: 1 }] },
  { id: 'a4', trader: 'Devon K.',  avatar: 4, account: 'FP-100K-13892', tier: 'Mid',          accountSize: 100000, openPnl: -680,  lastUpdate: 12, positions: [{ symbol: 'ES', side: 'short', pnl: -380, size: 2 }, { symbol: 'NQ', side: 'long', pnl: -300, size: 1 }] },
  { id: 'a5', trader: 'Felix W.',  avatar: 1, account: 'FP-50K-14672',  tier: 'Beginner',     accountSize: 50000,  openPnl: 480,   lastUpdate: 4, positions: [{ symbol: 'YM', side: 'long', pnl: 480, size: 1 }] },
  { id: 'a6', trader: 'Ana M.',    avatar: 2, account: 'FP-150K-13256', tier: 'Professional', accountSize: 150000, openPnl: 1840,  lastUpdate: 1, positions: [{ symbol: 'ES', side: 'long', pnl: 920, size: 2 }, { symbol: 'GC', side: 'long', pnl: 920, size: 1 }] },
  { id: 'a7', trader: 'Brent O.',  avatar: 3, account: 'FP-100K-14821', tier: 'Mid',          accountSize: 100000, openPnl: 0,     lastUpdate: 30, positions: [] },
  { id: 'a8', trader: 'Lisa H.',   avatar: 4, account: 'FP-50K-15034',  tier: 'Beginner',     accountSize: 50000,  openPnl: 240,   lastUpdate: 6, positions: [{ symbol: 'CL', side: 'long', pnl: 240, size: 1 }] },
  { id: 'a9', trader: 'Alex P.',   avatar: 1, account: 'FP-150K-14201', tier: 'Professional', accountSize: 150000, openPnl: -1240, lastUpdate: 3, positions: [{ symbol: 'ES', side: 'short', pnl: -680, size: 3 }, { symbol: 'NQ', side: 'short', pnl: -560, size: 1 }] },
];

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };

export function AdminTradingHallPage() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState(ACCOUNTS);
  const [paused, setPaused] = useState<string[]>([]);
  const [confirming, setConfirming] = useState<null | 'all' | string>(null);

  useEffect(() => {
    const t = setInterval(() => {
      setAccounts((curr) => curr.map((a) => ({
        ...a,
        openPnl: a.openPnl + (Math.random() - 0.5) * 20,
        lastUpdate: 0,
      })));
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const totalPnl = accounts.reduce((s, a) => s + a.openPnl, 0);
  const totalPositions = accounts.reduce((s, a) => s + a.positions.length, 0);
  const losers = accounts.filter((a) => a.openPnl < 0).length;
  const winners = accounts.filter((a) => a.openPnl > 0).length;

  function killAll() {
    setAccounts((curr) => curr.map((a) => ({ ...a, openPnl: 0, positions: [] })));
    setConfirming(null);
    alert('All positions killed across all accounts. Audit logged.');
  }

  function killOne(id: string) {
    setAccounts((curr) => curr.map((a) => (a.id === id ? { ...a, openPnl: 0, positions: [] } : a)));
    setConfirming(null);
  }

  function togglePause(id: string) {
    setPaused((curr) => curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]);
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Trading hall</div>
        <button onClick={() => setConfirming('all')} data-testid="kill-all" className="px-3 text-[15px] text-[#FF3B30]">Kill all</button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3 rounded-[16px] border border-white/10 bg-gradient-to-br from-[#34C759] to-[#248A3D] p-4 text-center">
        <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.85)' }}>Open P&L across all accounts</div>
        <div className="mt-1 text-[28px] font-bold tabular-nums" style={{ color: totalPnl >= 0 ? 'white' : '#FF3B30' }}>{totalPnl >= 0 ? '+' : ''}${totalPnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>{totalPositions} open positions · {winners}W / {losers}L</div>
      </div>

      {/* Live indicator */}
      <div className="mx-4 mt-3 flex items-center justify-center gap-2 text-[10px] text-white/60">
        <span className="h-1.5 w-1.5 rounded-full bg-[#34C759] animate-pulse" /> Live · auto-refresh every 3s
      </div>

      {/* Quick stats */}
      <div className="mx-4 mt-2 grid grid-cols-4 gap-2">
        <Stat label="Active"  value={accounts.filter((a) => a.positions.length > 0).length} color="#0A84FF" />
        <Stat label="Winners" value={winners} color="#34C759" />
        <Stat label="Losers"  value={losers}  color="#FF3B30" />
        <Stat label="Paused"  value={paused.length} color="#FF9500" />
      </div>

      {/* Account grid */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-white/50">Live accounts</h2>
      <div className="px-4 grid grid-cols-2 gap-2">
        {accounts.map((a) => {
          const isP = paused.includes(a.id);
          const positive = a.openPnl >= 0;
          return (
            <div key={a.id} data-testid={`hall-${a.id}`} className={['rounded-[12px] border bg-white/5 p-2.5', isP ? 'border-[#FF9500]' : 'border-white/10'].join(' ')}>
              <div className="flex items-center gap-1.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold" style={{ backgroundColor: TIER_COLOR[a.tier] + '30', color: TIER_COLOR[a.tier] }}>{a.trader[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold text-white truncate">{a.trader}</div>
                  <div className="text-[9px] text-white/40">${(a.accountSize/1000).toFixed(0)}K</div>
                </div>
                <div className="text-right">
                  <div className={['text-[12px] font-bold tabular-nums', positive ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>{positive ? '+' : ''}${a.openPnl.toFixed(0)}</div>
                </div>
              </div>
              {a.positions.length > 0 ? (
                <div className="mt-1.5 space-y-0.5">
                  {a.positions.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px]">
                      <span className={['font-bold', p.side === 'long' ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>{p.symbol} {p.side === 'long' ? '↑' : '↓'} {p.size}</span>
                      <span className={['tabular-nums', p.pnl >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'].join(' ')}>{p.pnl >= 0 ? '+' : ''}${p.pnl.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-1.5 text-[10px] text-white/30 italic">No open positions</div>
              )}
              <div className="mt-1.5 flex gap-1">
                <button onClick={() => togglePause(a.id)} data-testid={`pause-${a.id}`} className={['flex-1 h-6 rounded-[6px] text-[9px] font-bold', isP ? 'bg-[#FF9500] text-black' : 'bg-white/10 text-white'].join(' ')}>{isP ? '▶ Resume' : '⏸ Pause'}</button>
                <button onClick={() => setConfirming(a.id)} data-testid={`killone-${a.id}`} className="flex-1 h-6 rounded-[6px] bg-[#FF3B30] text-white text-[9px] font-bold active:opacity-40">⛔ Kill</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm sheets */}
      {confirming === 'all' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setConfirming(null)}>
          <div className="w-full max-w-[380px] rounded-[20px] bg-[#1C1C1E] p-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-[18px] font-bold mb-1 text-[#FF3B30]">⚠ KILL ALL POSITIONS</h2>
            <p className="text-[12px] text-white/60 mb-3">This will close all open positions across <span className="font-bold">all {accounts.filter((a) => a.positions.length > 0).length} active accounts</span>. Action is irreversible. All actions logged to audit.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(null)} className="flex-1 h-10 rounded-full bg-white/10 text-[13px] font-semibold active:opacity-40">Cancel</button>
              <button onClick={killAll} data-testid="confirm-kill-all" className="flex-1 h-10 rounded-full bg-[#FF3B30] text-white text-[13px] font-bold active:opacity-40">Yes, kill all</button>
            </div>
          </div>
        </div>
      )}

      {confirming && confirming !== 'all' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setConfirming(null)}>
          <div className="w-full max-w-[380px] rounded-[20px] bg-[#1C1C1E] p-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-[18px] font-bold mb-1">Kill positions?</h2>
            <p className="text-[12px] text-white/60 mb-3">Close all open positions for {accounts.find((a) => a.id === confirming)?.trader}?</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(null)} className="flex-1 h-10 rounded-full bg-white/10 text-[13px] font-semibold active:opacity-40">Cancel</button>
              <button onClick={() => killOne(confirming)} data-testid="confirm-kill-one" className="flex-1 h-10 rounded-full bg-[#FF3B30] text-white text-[13px] font-bold active:opacity-40">Kill positions</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
      <div className="text-[9px] text-white/60 uppercase">{label}</div>
      <div className="text-[16px] font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
